/**
 * Shared helper for the platform's public (no-auth) config endpoints — e.g.
 * `/platform/maintenance` (MaintenanceContext) and `/platform/session-timeout`
 * (sessionActivity, #181). Both are polled on app boot before auth is
 * established, so they share one raw-fetch/ok/json scaffold instead of each
 * re-hardcoding VITE_API_URL and a try/catch.
 *
 * Deliberately uses a raw `fetch` against VITE_API_URL rather than the `api`
 * (axios) instance — these calls run before login and axios carries auth
 * interceptor/cookie behavior these endpoints don't need. Applies a timeout
 * so a stalled request (proxy black-hole, cold start) can't hang the caller
 * indefinitely.
 */
const DEFAULT_TIMEOUT_MS = 3000

/**
 * Fetch a public platform config endpoint. Resolves to the parsed JSON body,
 * or `null` if the request fails, times out, or the response is not ok —
 * callers should fall back to a cached/default value in that case.
 */
export async function fetchPublicPlatformConfig(path, { timeoutMs = DEFAULT_TIMEOUT_MS } = {}) {
  try {
    const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
    const response = await fetch(`${baseURL}${path}`, {
      signal: AbortSignal.timeout(timeoutMs),
    })
    if (!response.ok) return null
    return await response.json()
  } catch (error) {
    console.debug(`Could not fetch platform config from ${path}:`, error?.message)
    return null
  }
}
